import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import {
	onGetMatchSearch,
	onGetMyNewMatches,
	onGetReportSubject,
	onConnectSocket,
	onEmitLike,
	onDisconnectSocketMatch as onDisconnectSocket,
	onGetMatchesPartner,
} from "./MatchThunk";
import { MatchesSearch } from "../../domain/entities/userUnjabbed";
import { Likes } from "../../domain/entities/userMatch";
import { ReportSubject } from "../../domain/entities/getReportSubject";
import { LikeParams } from "../../domain/entities/emitLikeParams";
import InfoNotificationAdmin from "../../../notification/domain/entities/InfoNotificationAdmin";
import InfoNotificationAlert from "../../../admin/domain/entities/InfoNotificationAlert";

interface MatchState {
	isLoading: boolean;
	loading: boolean;
	matchesLoading: boolean;
	matchesPartnerLoading: boolean;
	likeLoading: boolean;
	myLikesLoading: boolean;
	myMatchesLoading: boolean;
	newMatchesLoading: boolean;
	error?: string;
	matchesSearch?: MatchesSearch;
	matchesSearchCount: number;
	reportSubject?: ReportSubject[];
	myNotifications: {
		like: Likes[];
		myLikes: Likes[];
		match: Likes[];
		alert: InfoNotificationAdmin[];
	};
	myMatches: Likes[];
	myNewMatches: Likes[];
	notifications: Array<LikeParams | InfoNotificationAlert>;
	currentUserIndex: number;
	currentRadio: number;
	currentPage: number;
	hasMore: boolean;
	isContinue: boolean;
	hasExecuted: boolean;
	searchVersion: number;
}

const initialState: MatchState = {
	isLoading: false,
	loading: false,
	matchesPartnerLoading: false,
	matchesLoading: false,
	newMatchesLoading: false,
	likeLoading: false,
	myLikesLoading: false,
	myMatchesLoading: false,
	error: undefined,
	matchesSearch: undefined,
	matchesSearchCount: 0,
	reportSubject: undefined,
	myNotifications: {
		like: [],
		myLikes: [],
		match: [],
		alert: [],
	},
	notifications: [],
	myMatches: [],
	myNewMatches: [],
	currentUserIndex: 0,
	currentRadio: 0,
	currentPage: 1,
	hasMore: true,
	isContinue: false,
	hasExecuted: false,
	searchVersion: 0,
};

const matchSlice = createSlice({
	name: "match",
	initialState,
	reducers: {
		setCurrenUserIndex: (state, action: PayloadAction<number>) =>{
			state.currentUserIndex = action.payload;
		},
		setCurrentRadio: (state, action: PayloadAction<number>) =>{
			state.currentRadio = action.payload;
		},
		setCurrentPage: (state, action: PayloadAction<number>) =>{
			state.currentPage = action.payload;
		},
		setHasMore: (state, action: PayloadAction<boolean>) =>{
			state.hasMore = action.payload;
		},
		setIsContinue: (state, action: PayloadAction<boolean>) =>{
			state.isContinue = action.payload;
		},
		setHasExecuted: (state, action: PayloadAction<boolean>) =>{
			state.hasExecuted = action.payload;
		},
		setMatchesLoading: (state, action: PayloadAction<boolean>) =>{
			state.matchesLoading = action.payload;
		},
		bumpSearchVersion: (state) =>{
			state.searchVersion += 1;
		},
		setResetSearch: (state) =>{
			state.currentUserIndex = 0;
			state.currentPage = 1;
			state.hasMore = true;
			state.isContinue = false;
			state.hasExecuted = false;
			state.matchesLoading = false;
			state.matchesSearch = undefined;
			state.searchVersion += 1;
		},
		notificationReceived: (state, action: PayloadAction<LikeParams | InfoNotificationAlert>) => {
			state.notifications.push(action.payload);
		},
		deleteNotificationReceived: (state) => {
			state.notifications = [];
		},
		deleteMyMatches: (state) => {
			state.myMatches = [];
		},
		myNotificationReceived: (
			state,
			action: PayloadAction<{
				type: "like" | "myLikes" | "match";
				data: Likes[];
			}>
		) => {
			const { type, data } = action.payload;
			const newNotifications = data.filter(
				(newNotif) => !state.myNotifications[type].some(
					(existingNotif) => existingNotif.user_id === newNotif.user_id
				)
			);
			state.myNotifications[type] = [
				...state.myNotifications[type],
				...newNotifications
			];
		},
		alertReceived: (
			state,
			action: PayloadAction<{
				data: InfoNotificationAdmin[];
			}>
		) => {
			const newAlerts = action.payload.data.filter(
				(newAlert) => !state.myNotifications.alert.some(
					(existingAlert) =>
						(existingAlert.id || existingAlert.notification_id) ===
						(newAlert.id || newAlert.notification_id)
				)
			);
			state.myNotifications.alert = [
				...state.myNotifications.alert,
				...newAlerts
			];
		},
		updateMyNotificationReceived: (
			state,
			action: PayloadAction<{
				type: "like" | "myLikes" | "match" | "alert";
				userMyNotification: Partial<Likes | InfoNotificationAdmin>;
			}>
		) => {
			const { type, userMyNotification } = action.payload;
			if (type === "alert") {
				state.myNotifications.alert = state.myNotifications.alert.map(item => {
					const incomingAlertId =
						"id" in userMyNotification
							? userMyNotification.id || userMyNotification.notification_id
							: undefined;
					if (
						((item.id || item.notification_id) &&
							incomingAlertId &&
							(item.id || item.notification_id) === incomingAlertId) ||
						(item.user_id && userMyNotification.user_id && item.user_id === userMyNotification.user_id)
					) {
						return { ...item, ...userMyNotification };
					}
					return item;
				});
				return;
			}
			state.myNotifications[type] = state.myNotifications[type].map(item => {
				if (item.user_id === userMyNotification.user_id) {
					return { ...item, ...userMyNotification };
				}
				return item;
			});
		},
		deleteMyNotificationAction: (
			state,
			action: PayloadAction<{
				type: "like" | "myLikes" | "match" | "alert";
				user_id: string;
			}>
		) => {
			if (action.payload.type === "alert") {
				state.myNotifications.alert = state.myNotifications.alert.filter(
					(notification) =>
						(notification.id || notification.notification_id) !== action.payload.user_id
				);
				return;
			}
			if (state.myNotifications[action.payload.type]) {
				state.myNotifications[action.payload.type] = state.myNotifications[
					action.payload.type
				].filter(
					(notification) => notification.user_id !== action.payload.user_id
				);
			}
		},
		deleteMyNotificationReceived: (state) => {
			state.myNotifications = {
				like: [],
				myLikes: [],
				match: [],
				alert: [],
			};
		},
		updateMatchesSearch: (
			state,
			action: PayloadAction<MatchesSearch | undefined>
		) => {
			state.matchesSearch = action.payload;
		},
		deleteNewMatch: (
			state,
			action: PayloadAction<string>
		) => {
			state.myNewMatches = state.myNewMatches.filter(
			  (match) => match.user_id !== action.payload
			);
		},
		addNewMatch: (state, action: PayloadAction<Likes>) => {
			const exists = state.myNewMatches.some(match => match.user_id === action.payload.user_id);
			if (!exists) state.myNewMatches.unshift(action.payload);
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(onGetMatchSearch.pending, (state) => {})
			.addCase(
				onGetMatchSearch.fulfilled,
				(state, action) => {
					if ((action.meta.arg.clientSearchVersion ?? 0) !== state.searchVersion) {
						return;
					}
					if (action.payload.clientResolvedRadio) {
						state.currentRadio = action.payload.clientResolvedRadio;
					}
					if (!state.matchesSearch || !state.matchesSearch.matches.length) {
						state.matchesSearch = action.payload;
					} else {
						const existingMatches = state.matchesSearch.matches;
						const newMatches = action.payload.matches;

						const existingMatchIds = new Set(
							existingMatches.map((match) => match.id)
						);
						
						const nonDuplicateMatches = newMatches.filter(
							(match) => !existingMatchIds.has(match.id)
						);
						
						state.matchesSearch.matches = [
							...existingMatches,
							...nonDuplicateMatches,
						];
						state.matchesSearch.page = action.payload.page;
					}
					state.matchesSearchCount = action.payload.sourceMatchesCount || 0;
				}
			)
			.addCase(onGetMatchSearch.rejected, (state, action) => {
				state.error = action.error.message;
			})
			.addCase(onGetMyNewMatches.pending, (state) => {
				state.newMatchesLoading = true;
			})
			.addCase(
				onGetMyNewMatches.fulfilled,
				(state, action: PayloadAction<Likes[]>) => {
					state.newMatchesLoading = false;
					const newMatches = action.payload.filter(
						(newMatch) => !state.myNewMatches.some(
							(existingMatch) => existingMatch.user_id === newMatch.user_id
						)
					);
					state.myNewMatches = [...state.myNewMatches, ...newMatches];
				}
			)
			.addCase(onGetMyNewMatches.rejected, (state, action) => {
				state.error = action.error.message;
				state.newMatchesLoading = false;
			})
			.addCase(onGetReportSubject.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(
				onGetReportSubject.fulfilled,
				(state, action: PayloadAction<ReportSubject[]>) => {
					state.isLoading = false;
					state.reportSubject = action.payload;
				}
			)
			.addCase(onGetReportSubject.rejected, (state, action) => {
				state.error = action.error.message;
				state.isLoading = false;
			})
			.addCase(onConnectSocket.pending, (state) => {
				state.loading = true;
			})
			.addCase(onConnectSocket.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(onConnectSocket.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message;
			})
			.addCase(onEmitLike.pending, (state) => {
				state.loading = true;
			})
			.addCase(onEmitLike.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(onEmitLike.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message;
			})
			.addCase(onDisconnectSocket.pending, (state) => {
				state.loading = true;
			})
			.addCase(onDisconnectSocket.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(onDisconnectSocket.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message;
			})

			.addCase(onGetMatchesPartner.pending, (state) => {
				state.matchesPartnerLoading = true;
			})
			.addCase(onGetMatchesPartner.fulfilled, 
				(state,action: PayloadAction<Likes[]>) => {
				state.matchesPartnerLoading = false;
				state.myMatches = action.payload;
			})
			.addCase(onGetMatchesPartner.rejected, (state, action) => {
				state.error = action.error.message;
				state.matchesPartnerLoading = false;
			})
			;
	},
});
export const {
	notificationReceived,
	myNotificationReceived,
	updateMatchesSearch,
	deleteMyNotificationReceived,
	deleteMyNotificationAction,
	deleteNotificationReceived,
	deleteNewMatch,
	updateMyNotificationReceived,
	alertReceived,
	addNewMatch,
	setCurrenUserIndex,
	setCurrentRadio,
	setCurrentPage,
	setResetSearch,
	setHasMore,
	setIsContinue,
	setHasExecuted,
	setMatchesLoading,
	bumpSearchVersion,
	deleteMyMatches,
} = matchSlice.actions;
export default matchSlice.reducer;
